import { Machine, MachineConfig, assign, sendParent } from 'xstate';

export interface LightboxImage {
  id: string;
  screenshotId: string;
  enhanced: boolean;
  imgUrl: string;
}

export interface LightboxContext {
  index: number;
  imageData: LightboxImage[];
}

interface LightboxSchema {
  context: {
    index: number;
    imageData: [];
  };
  states: {
    unknown: {};
    single: {};
    first: {};
    last: {};
    middle: {};
  };
}

export enum Direction {
  NEXT = '1',
  PREVIOUS = '-1',
}

type LightboxEvent = { type: Direction.NEXT } | { type: Direction.PREVIOUS };

const lightboxMachineConfig: MachineConfig<
  LightboxContext,
  LightboxSchema,
  LightboxEvent
> = {
  initial: 'unknown',
  context: {
    index: 0,
    imageData: [],
  },
  states: {
    unknown: {
      on: {
        '': [
          { target: 'single', cond: 'isSingle' },
          { target: 'first', cond: 'isFirst' },
          { target: 'middle', cond: 'isMiddle' },
          { target: 'last', cond: 'isLast' },
        ],
      },
    },
    single: {},
    first: {
      on: {
        [Direction.NEXT]: {
          target: 'unknown',
          actions: ['goNext', 'sendNavigateMetric'],
        },
      },
    },
    middle: {
      on: {
        [Direction.NEXT]: {
          target: 'unknown',
          actions: ['goNext', 'sendNavigateMetric'],
        },
        [Direction.PREVIOUS]: {
          target: 'unknown',
          actions: ['goBack', 'sendNavigateMetric'],
        },
      },
    },
    last: {
      on: {
        [Direction.PREVIOUS]: {
          target: 'unknown',
          actions: ['goBack', 'sendNavigateMetric'],
        },
      },
    },
  },
};

export const guards = {
  isSingle: (context: LightboxContext) => context.imageData.length === 1,
  isFirst: (context: LightboxContext) => context.index === 0,
  isLast: (context: LightboxContext) =>
    context.index === context.imageData.length - 1,
  isMiddle: (context: LightboxContext) =>
    context.index > 0 && context.index < context.imageData.length - 1,
};

export const actions = {
  goNext: assign((context: LightboxContext) => ({
    index: context.index + Number(Direction.NEXT),
  })),
  goBack: assign((context: LightboxContext) => ({
    index: context.index + Number(Direction.PREVIOUS),
  })),
  sendNavigateMetric: sendParent<LightboxContext, LightboxEvent>(
    'LIGHTBOX_NAVIGATION',
  ),
};

const lightboxMachine = Machine(lightboxMachineConfig, { guards, actions });

export const generateLightboxMachine = (context: LightboxContext) => {
  return lightboxMachine.withContext(context);
};

export default lightboxMachine;
